import React from 'react';

import { Wizard, WizardStep } from 'src/hooks/useWizard';

import {
  ContractBasicDetailsModal,
  ContractBasicDetailsModalState,
} from './contractBasicDetailsModal/ContractBasicDetailsModal';
import {
  ContractConfigReviewModal,
  ContractConfigReviewModalState,
} from './contractConfigReviewModal/ContractConfigReviewModal';
import {
  ContractFilterConfigModal,
  ContractFilterConfigModalState,
} from './contractFilterConfigModal/ContractFilterConfigModal';
import {
  ContractLaunchDetailsModal,
  ContractLaunchDetailsModalState,
} from './contractLaunchDetailsModal/ContractLaunchDetailsModal';
import { ContractConfigStep, ContractConfigWorkflow } from './steps';

type ContractConfigWizardState = ContractBasicDetailsModalState &
  ContractFilterConfigModalState &
  ContractConfigReviewModalState &
  ContractLaunchDetailsModalState;

interface Props {
  isOpen: boolean;
  onClose: () => void;
  __test?: {
    initialStepId?: ContractConfigStep;
    initialState?: ContractConfigWizardState;
  };
}

export const ContractConfigWizard = ({ isOpen, onClose, __test }: Props) => {
  const wizardSteps = React.useMemo(
    (): MinLengthArray<1, WizardStep<ContractConfigWizardState>> => [
      {
        id: ContractConfigStep.BasicDetails,
        name: 'Basic Details',
        content: ({ state, mergeState, goToNextStep, reset }) => (
          <ContractBasicDetailsModal
            isOpen={isOpen}
            state={state}
            onGraphIdChange={(graphId) => mergeState({ graphId })}
            onContractVariantChange={(contractVariant) =>
              mergeState({ contractVariant })
            }
            onSourceVariantChange={(sourceVariant) =>
              mergeState({ sourceVariant })
            }
            onGraphTitleChange={(graphTitle) => mergeState({ graphTitle })}
            onComplete={() => goToNextStep()}
            onClose={() => {
              reset();
              onClose();
            }}
          />
        ),
      },
      {
        id: ContractConfigStep.FilterConfig,
        name: 'Contract Filters',
        content: ({
          state,
          mergeState,
          goToNextStep,
          goToPreviousStep,
          reset,
        }) => (
          <ContractFilterConfigModal
            isOpen={isOpen}
            state={state}
            onSchemaLoaded={({ key, schemaValue }) =>
              mergeState({ [key]: schemaValue })
            }
            onIncludedTagsChange={(includedTags) =>
              mergeState({ includedTags })
            }
            onExcludedTagsChange={(excludedTags) =>
              mergeState({ excludedTags })
            }
            onHideUnreachableTypesChange={(hideUnreachableTypes) =>
              mergeState({ hideUnreachableTypes })
            }
            saveAllTags={(allTags) => {
              mergeState({
                allTags,
              });
            }}
            onContractPreviewTrigger={() =>
              // clear stale preview data
              mergeState({
                proposedSchema: undefined,
                previewErrors: undefined,
              })
            }
            onContractPreviewResult={({
              sourceSchema,
              latestSchema,
              proposedSchema,
              previewErrors,
            }) =>
              mergeState({
                sourceSchema,
                latestSchema,
                proposedSchema,
                previewErrors,
              })
            }
            onPrevious={() => goToPreviousStep()}
            onComplete={() => goToNextStep()}
            onClose={() => {
              reset();
              onClose();
            }}
            workflow={ContractConfigWorkflow.create}
          />
        ),
      },
      {
        id: ContractConfigStep.ReviewContract,
        name: 'Review Contract',
        content: ({
          state,
          mergeState,
          goToNextStep,
          goToPreviousStep,
          reset,
        }) => (
          <ContractConfigReviewModal
            isOpen={isOpen}
            state={state}
            onInitiateLaunchChange={(initiateLaunch) =>
              mergeState({ initiateLaunch })
            }
            onPrevious={() => goToPreviousStep()}
            onComplete={() => goToNextStep()}
            onClose={() => {
              reset();
              onClose();
            }}
            workflow={ContractConfigWorkflow.create}
          />
        ),
      },
      {
        id: ContractConfigStep.ContractCreated,
        name: 'Contract Created',
        content: ({ state, reset }) => (
          <ContractLaunchDetailsModal
            isOpen={isOpen}
            state={state}
            onClose={() => {
              reset();
              onClose();
            }}
            workflow={ContractConfigWorkflow.create}
          />
        ),
      },
    ],
    [isOpen, onClose],
  );
  return <Wizard<ContractConfigWizardState> {...__test} steps={wizardSteps} />;
};
